import { detectBrowser } from './utils';
import { startRecording, stopRecording, getLogs, setUserInfo, captureAuthAttemptContext, getAuthAttemptContext, clearAuthAttemptContext, getAuthAttemptCount } from './activity-logger';
import logger, { LogLevel } from '@/lib/logging';
import { isDiagnosticLoggingEnabled, getClientLogLevel } from '@/components/feedback-button/lib/env-service';

// Re-export specific activity logger functions that this module uses/exposes
export { setUserInfo, captureAuthAttemptContext, clearAuthAttemptContext, getAuthAttemptCount };

const originalConsole = console;

/**
 * Enhanced error type detection
 * Provides specific error types instead of generic "login_error"
 */
function getDetailedErrorType(logs: any[], errorLog: any): string {
    if (!errorLog) return 'unknown_error';

    const message = errorLog.message.toLowerCase();

    // Firebase Auth error codes (specific)
    if (message.includes('auth/popup-closed-by-user')) return 'oauth_popup_closed';
    if (message.includes('auth/cancelled-popup-request')) return 'oauth_popup_cancelled';
    if (message.includes('auth/popup-blocked')) return 'oauth_popup_blocked';
    if (message.includes('auth/network-request-failed')) return 'oauth_network_error';
    if (message.includes('auth/timeout')) return 'oauth_timeout';
    if (message.includes('auth/unauthorized-domain')) return 'firebase_config_error';
    if (message.includes('auth/invalid-api-key')) return 'firebase_config_error';
    if (message.includes('auth/app-not-authorized')) return 'firebase_config_error';

    // Generic auth error with code
    if (message.includes('auth/')) {
        const match = message.match(/auth\/([\w-]+)/);
        return match ? match[0].replace('auth/', 'auth_error_') : 'auth_error';
    }

    // Session creation errors
    if (message.includes('/api/auth/login') && message.includes('failed')) return 'session_creation_failed';
    if (message.includes('session') && (message.includes('failed') || message.includes('error'))) return 'session_error';
    if (message.includes('cookie') && message.includes('failed')) return 'session_cookie_error';

    // Network errors
    if (message.includes('fetch') && message.includes('failed')) return 'network_fetch_error';
    if (message.includes('network')) return 'network_error';
    if (message.includes('cors')) return 'cors_error';
    if (message.includes('timeout')) return 'network_timeout';

    // Permission errors
    if (message.includes('permission')) return 'permission_denied';
    if (message.includes('forbidden') || message.includes('403')) return 'access_forbidden';

    // Browser/environment errors
    if (message.includes('blocked') || message.includes('blocker')) return 'browser_blocked';
    if (message.includes('cookie') && message.includes('disabled')) return 'cookies_disabled';

    // Check if cookies are disabled (from context)
    const authContext = getAuthAttemptContext();
    if (authContext && !authContext.cookiesEnabled) {
        return 'cookies_disabled';
    }

    // Default to login_error if we can't be more specific
    return 'login_error';
}

export const authDebugShim = {
    startCapture: async (reason: string) => {
        // Check if diagnostic logging is enabled in app settings
        const isEnabled = await isDiagnosticLoggingEnabled();

        if (!isEnabled) {
            originalConsole.log(`[AuthDebug] Capture not started - diagnostic logging is disabled in settings`);
            return false;
        }

        startRecording(undefined, undefined, 'system');
        originalConsole.log(`[AuthDebug] Capture started - Reason: ${reason}`);
        return true;
    },
    stopCapture: () => stopRecording(),

    hasError: () => {
        const logs = getLogs();
        return logs.some(log => log.level === 'error');
    },

    getErrorType: () => {
        const logs = getLogs();
        const errorLog = logs.find(log => log.level === 'error');
        if (!errorLog) return null;

        // Try to extract a code or meaningful type
        const message = errorLog.message.toLowerCase();
        if (message.includes('auth/')) {
            const match = message.match(/auth\/[\w-]+/);
            return match ? match[0] : 'auth_error';
        }
        if (message.includes('permission') || message.includes('denied')) return 'permission_denied';
        if (message.includes('network') || message.includes('fetch')) return 'network_error';

        return 'general_error';
    },

    stopCaptureAndSubmitIfError: async () => {
        const logs = stopRecording();
        const hasError = logs.some(log => log.level === 'error');

        if (!hasError) {
            return { submitted: false };
        }

        // Get pre-auth context if available
        const authContext = getAuthAttemptContext();
        const attemptCount = getAuthAttemptCount();

        // Generate report
        const errorLog = logs.find(log => log.level === 'error');
        const allErrors = logs.filter(log => log.level === 'error');
        const errorType = getDetailedErrorType(logs, errorLog);

        // Get clear browser info
        const browserInfo = detectBrowser();
        const browserString = `${browserInfo.name} ${browserInfo.version}`;

        // Calculate OAuth duration from logs
        const startLog = logs.find(l => l.message.includes('GOOGLE SIGN-IN ATTEMPT') && l.message.includes('STARTED'));
        const failLog = logs.find(l => l.message.includes('GOOGLE SIGN-IN FAILED'));
        let oauthDurationMs: number | null = null;
        if (startLog && failLog) {
            oauthDurationMs = new Date(failLog.timestamp).getTime() - new Date(startLog.timestamp).getTime();
        }

        // Detect mobile/device from User-Agent
        const ua = authContext?.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
        const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
        const isIOS = /iPhone|iPad|iPod/i.test(ua);
        const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Line|WeChat|Snapchat/i.test(ua);

        let report = `# Auth Debug Report\n\n`;

        // Quick diagnosis box
        report += `> **🔍 Quick Diagnosis**\n`;
        report += `> - Error: \`${errorType}\`\n`;
        if (oauthDurationMs !== null) {
            report += `> - OAuth Duration: ${oauthDurationMs}ms`;
            if (oauthDurationMs < 1000) report += ` ⚡ (very fast - likely blocked)`;
            else if (oauthDurationMs < 5000) report += ` ⏱️ (quick - likely user cancelled)`;
            else if (oauthDurationMs > 30000) report += ` 🐌 (slow - likely timeout)`;
            report += `\n`;
        }
        report += `> - Device: ${isMobile ? '📱 Mobile' : '🖥️ Desktop'}${isIOS ? ' (iOS)' : ''}${isInAppBrowser ? ' ⚠️ IN-APP BROWSER' : ''}\n`;
        report += `> - Errors Found: ${allErrors.length}\n`;
        report += `> - Total Logs: ${logs.length}\n\n`;

        // Enhanced context section
        report += `## Authentication Attempt Context\n\n`;
        if (authContext) {
            report += `* **Attempt Number**: ${attemptCount} (${attemptCount === 1 ? 'First attempt' : `${attemptCount} failed attempts`})\n`;
            report += `* **Auth Method**: ${authContext.authMethod || 'unknown'}\n`;
            report += `* **URL**: ${authContext.url}\n`;
            report += `* **Referrer**: ${authContext.referrer}\n`;
            report += `* **Timestamp**: ${authContext.timestamp}\n`;
            if (oauthDurationMs !== null) {
                report += `* **OAuth Duration**: ${oauthDurationMs}ms\n`;
            }
            report += `* **Browser**: ${browserString}\n`;
            report += `* **User Agent**: \`${authContext.userAgent}\`\n`;
            report += `* **Platform**: ${authContext.platform}\n`;
            report += `* **Screen**: ${authContext.screenSize} (viewport: ${authContext.viewport})\n`;
            report += `* **Language**: ${authContext.language}\n`;
            report += `* **Timezone**: ${authContext.timezone}\n`;
            report += `* **Cookies Enabled**: ${authContext.cookiesEnabled ? '✅ Yes' : '❌ NO - POTENTIAL ISSUE'}\n`;
            report += `* **Is Mobile**: ${isMobile ? '📱 Yes' : 'No'}\n`;
            report += `* **Is iOS**: ${isIOS ? 'Yes' : 'No'}\n`;
            report += `* **Is In-App Browser**: ${isInAppBrowser ? '⚠️ YES' : 'No'}\n`;
        } else {
            report += `* **URL**: ${typeof window !== 'undefined' ? window.location.href : 'unknown'}\n`;
            report += `* **Browser**: ${browserString}\n`;
            report += `* **User Agent**: \`${typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'}\`\n`;
            report += `* **Attempt Count**: ${attemptCount || 'unknown'}\n`;
        }
        report += `* **Error Type**: \`${errorType}\`\n\n`;

        // Diagnostic summary
        report += `## Diagnostic Summary\n\n`;
        report += `| Category | Log Count | Status |\n`;
        report += `|---|---|---|\n`;

        const categories = ['auth', 'role', 'access', 'network', 'general', 'ui'] as const;
        categories.forEach(cat => {
            const count = logs.filter(l => l.category === cat).length;
            const hasCatError = logs.some(l => l.category === cat && l.level === 'error');
            const status = hasCatError ? '❌ Error' : (count > 0 ? '✅ OK' : '⚪ None');
            report += `| ${cat.charAt(0).toUpperCase() + cat.slice(1)} | ${count} | ${status} |\n`;
        });
        report += `\n`;

        // Error analysis (all errors, not just first)
        report += `## Error Analysis\n\n`;
        if (allErrors.length > 0) {
            report += `### All Errors (${allErrors.length})\n\n`;
            allErrors.forEach((err, i) => {
                report += `${i + 1}. \`${err.timestamp}\` [${err.category?.toUpperCase() || 'GEN'}] ${err.message.substring(0, 200)}\n`;
            });
            report += `\n`;

            report += `### Primary Error\n\n`;
            report += `\`\`\`\n${errorLog?.message || 'unknown'}\n\`\`\`\n\n`;

            // Provide context-specific insights
            if (errorType.includes('popup_closed') || errorType.includes('popup_cancelled')) {
                report += `**Likely Cause**: User cancelled the OAuth popup or popup was blocked by browser.\n\n`;
                report += `**Recommendations**:\n`;
                report += `- ${isMobile ? '🔴 **SWITCH TO REDIRECT FLOW** for mobile devices' : 'Check if popup blockers are enabled'}\n`;
                report += `- ${isInAppBrowser ? '🔴 **User must open in default browser** - in-app browsers block popups' : 'Consider using redirect auth flow as fallback'}\n`;
                report += `- Add better UX messaging for popup-required auth\n\n`;
            } else if (errorType.includes('popup_blocked')) {
                report += `**Likely Cause**: Browser or extension actively blocked the popup window.\n\n`;
                report += `**Recommendations**:\n`;
                report += `- ${isMobile ? '🔴 **SWITCH TO REDIRECT FLOW** for mobile devices' : 'Prompt user to allow popups for this site'}\n`;
                report += `- Implement automatic fallback to redirect flow on popup block\n`;
                report += `- Show clear instructions for enabling popups\n\n`;
            } else if (errorType.includes('network')) {
                report += `**Likely Cause**: Network connectivity issue during authentication.\n\n`;
                report += `**Recommendations**:\n`;
                report += `- Implement retry logic with exponential backoff\n`;
                report += `- Check server-side logs for timeouts\n`;
                report += `- Add network status detection and offline message\n\n`;
            } else if (!authContext?.cookiesEnabled) {
                report += `**⚠️ CRITICAL**: Cookies are disabled in the browser!\n\n`;
                report += `**Recommendations**:\n`;
                report += `- Show warning to user that cookies are required\n`;
                report += `- Provide instructions for enabling cookies\n\n`;
            } else if (errorType.includes('config_error')) {
                report += `**Likely Cause**: Firebase configuration issue (unauthorized domain, invalid API key, etc.).\n\n`;
                report += `**Recommendations**:\n`;
                report += `- Verify Firebase Console > Authentication > Settings > Authorized domains\n`;
                report += `- Check API key restrictions in Google Cloud Console\n`;
                report += `- Ensure the correct Firebase config is deployed\n\n`;
            }
        }

        report += `## Activity Logs (last 80)\n\n\`\`\`\n`;
        logs.slice(-80).forEach(l => {
            const emoji = l.level === 'error' ? '❌' : (l.level === 'warn' ? '⚠️' : 'ℹ️');
            report += `${l.timestamp} ${emoji} [${l.category?.toUpperCase() || 'GEN'}] ${l.message}\n`;
        });
        report += `\`\`\`\n`;

        // Automatic GitHub issue creation has been removed. Logs are flushed via stopRecording.
        // Users can create issues manually via the Feedback button.
        originalConsole.log('ℹ️ [AuthDebug] Error captured. Use the Feedback button to create an issue if needed.');
        return { submitted: false };
    },

    completeAuthSuccess: () => stopRecording(),
};

export const startCapture = authDebugShim.startCapture;
export const stopCapture = authDebugShim.stopCapture;
export const stopCaptureAndSubmitIfError = authDebugShim.stopCaptureAndSubmitIfError;
export const completeAuthSuccess = authDebugShim.completeAuthSuccess;
export const hasError = authDebugShim.hasError;
export const getErrorType = authDebugShim.getErrorType;
export const downloadLogs = () => {
    const logs = getLogs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auth-debug-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const clearSettingsCache = async () => {
    try {
        const level = await getClientLogLevel();
        logger.setLevel(level as LogLevel);
    } catch (error) {
        // Silently fail or log to original console if needed
    }
};
